import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

function translateAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) {
    return 'Correo o contraseña incorrectos.';
  }
  if (message.includes('Email not confirmed')) {
    return 'Tu correo aún no está confirmado. Revisa tu bandeja de entrada.';
  }
  if (message.includes('rate limit') || message.includes('too many')) {
    return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
  }
  return `No se pudo iniciar sesión. Inténtalo de nuevo. [${message}]`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetSuccess = (location.state as { resetSuccess?: boolean } | null)?.resetSuccess;

  // Si ya hay sesión activa, no hace falta mostrar el login.
  if (session) {
    return <Navigate to="/admin" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');

    if (!isSupabaseConfigured || !supabase) {
      setError('La conexión con Supabase no está configurada.');
      return;
    }
    if (!email.trim() || !password) {
      setError('Ingresa tu correo y contraseña.');
      return;
    }

    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setSubmitting(false);

    if (authError) {
      setError(translateAuthError(authError.message));
      return;
    }

    // Destino validado: solo rutas internas que empiecen con "/" y no sean auth.
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    const safeDestination =
      from && from.startsWith('/') && !from.startsWith('/login') ? from : '/admin';
    navigate(safeDestination, { replace: true });
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Marca */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-accent-500 flex items-center justify-center mb-4">
            <i className="ri-heart-pulse-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 font-heading">PCP Admin</h1>
          <p className="text-sm text-foreground-500 mt-1">Paticas con Propósito · Panel de administración</p>
        </div>

        {/* Tarjeta */}
        <div className="bg-white rounded-2xl border border-background-200 p-8">
          <h2 className="text-lg font-semibold text-foreground-900">Iniciar sesión</h2>
          <p className="text-sm text-foreground-500 mt-1 mb-6">Ingresa tus credenciales para acceder.</p>

          {resetSuccess && (
            <div className="mb-5 flex items-start gap-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg px-3 py-2.5">
              <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>
              <span>Tu contraseña fue actualizada. Ya puedes iniciar sesión.</span>
            </div>
          )}

          {error && (
            <div className="mb-5 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@paticasconproposito.com"
                className="w-full text-sm px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full text-sm px-4 py-2.5 pr-11 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/auth/forgot-password"
                className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full text-sm font-semibold py-2.5 px-4 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitting ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-foreground-500 hover:text-accent-600 transition-colors inline-flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line"></i>Volver a la tienda
          </Link>
        </div>
      </div>
    </div>
  );
}